import { fetch } from 'whatwg-fetch';
import { API_URLS } from './config';
import flatten from 'flatten';
import { isWasmRelated, TargetPlatforms } from './utils/platforms';
import {
  findSecurityException,
  getExceptionCauses,
  processErrors,
  processJUnitResults,
  processJVMOutput,
  processJVMOutputWithSql,
} from './view/output-view';
import { generateRequestId, getGa4ClientId } from './utils/request-id';

/**
 * @typedef {Object} KotlinVersion
 * @property {string} version
 * @property {string} build
 * @property {boolean} obsolete
 * @property {boolean} latestStable
 * @property {boolean} hasScriptJar
 * @property {string|null} stdlibVersion
 */

const CACHE = {
  compilerVersions: null,
};
const DEFAULT_FILE_NAME = 'File.kt';

/**
 * Generate a frontend ID and track the compile/run event in GA4
 * Uses a hybrid approach:
 * - Sets last_frontend_id as a user property (persists across events)
 * - Sends frontend_id as an event parameter (for precise correlation)
 * @param {string} tagName - The GA4 event name to track
 * @param {string} slug - The frontend slug for tracking (optional)
 * @param {string} page - The frontend page for tracking (optional)
 * @returns {string} The generated frontend ID
 */
function generateAndTrackFrontendId(tagName, slug, page) {
  const frontendId = generateRequestId();
  
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // Set as user property - persists across all events for this user session
    window.gtag('set', 'user_properties', {
      last_frontend_id: frontendId
    });
    
    // Build event parameters
    const eventParams = {
      frontend_id: frontendId,
      language: 'kotlin',
      playground_feature: 'exoquery',
    };
    
    // Add slug and page if provided
    if (slug) {
      eventParams.frontend_slug = slug;
    }
    if (page) {
      eventParams.frontend_page = page;
    }
    
    // Also send as event parameter for precise event-to-log correlation
    window.gtag('event', tagName, eventParams);
  }
  
  return frontendId;
}

export default class WebDemoApi {
  /**
   * @return {Promise<Array<KotlinVersion>>}
   */
  static getCompilerVersions() {
    if (!CACHE.compilerVersions) {
      CACHE.compilerVersions = fetch(API_URLS.VERSIONS)
        .then((response) => response.json())
        .catch(() => (CACHE.compilerVersions = null));
    }

    return CACHE.compilerVersions;
  }

  /**
   * Request on translation Kotlin code to JS code
   *
   * @param code            - string
   * @param compilerVersion - string kotlin compiler
   * @param platform        - TargetPlatform
   * @param args            - command line arguments
   * @param hiddenDependencies   - read only additional files
   * @param schema          - database schema (optional)
   * @returns {*|PromiseLike<T>|Promise<T>}
   */
  static translateKotlinToJs(
    code,
    compilerVersion,
    platform,
    args,
    hiddenDependencies,
    schema,
  ) {
    const MINIMAL_VERSION_IR = '1.5.0';
    const MINIMAL_VERSION_WASM = '1.9.0';
    const MINIMAL_VERSION_SWIFT_EXPORT = '2.0.0';

    if (
      platform === TargetPlatforms.JS_IR &&
      compilerVersion < MINIMAL_VERSION_IR
    ) {
      return Promise.resolve({
        output: '',
        errors: [
          {
            severity: 'ERROR',
            message: `JS IR compiler backend accessible only since ${MINIMAL_VERSION_IR} version`,
          },
        ],
        jsCode: '',
      });
    }

    if (isWasmRelated(platform) && compilerVersion < MINIMAL_VERSION_WASM) {
      return Promise.resolve({
        output: '',
        errors: [
          {
            severity: 'ERROR',
            message: `Wasm compiler backend accessible only since ${MINIMAL_VERSION_WASM} version`,
          },
        ],
        jsCode: '',
      });
    }

    if (
      platform === TargetPlatforms.SWIFT_EXPORT &&
      compilerVersion < MINIMAL_VERSION_SWIFT_EXPORT
    ) {
      return Promise.resolve({
        output: '',
        errors: [
          {
            severity: 'ERROR',
            message: `Swift export accessible only since ${MINIMAL_VERSION_SWIFT_EXPORT} version`,
          },
        ],
        jsCode: '',
      });
    }

    const frontendId = generateAndTrackFrontendId('translate_run');
    
    return executeCode(
      API_URLS.COMPILE(platform, compilerVersion),
      code,
      compilerVersion,
      platform,
      args,
      hiddenDependencies,
      schema,
      null,
      frontendId,
    ).then(function (data) {
      let output = '';
      let errorsAndWarnings = flatten(Object.values(data.errors));
      return {
        output: output,
        errors: errorsAndWarnings,
        jsCode: data.jsCode,
        wasm: data.wasm,
      };
    });
  }

  /**
   * Request on execute Kotlin code.
   *
   * @param code            - string
   * @param compilerVersion - string kotlin compiler
   * @param platform        - TargetPlatform
   * @param args            - command line arguments
   * @param theme           - theme of editor
   * @param hiddenDependencies   - read only additional files
   * @param onTestPassed    - function will call after test's passed
   * @param onTestFailed    - function will call after test's failed
   * @param schema          - database schema (optional)
   * @param annotation      - annotation string for tracking (optional)
   * @param slug            - frontend slug for tracking (optional)
   * @param page            - frontend page for tracking (optional)
   * @returns {*|PromiseLike<T>|Promise<T>}
   */
  static executeKotlinCode(
    code,
    compilerVersion,
    platform,
    args,
    theme,
    hiddenDependencies,
    onTestPassed,
    onTestFailed,
    schema,
    annotation,
    slug,
    page,
  ) {
    const frontendId = generateAndTrackFrontendId('compile_run', slug, page);
    
    return executeCode(
      API_URLS.COMPILE(platform, compilerVersion),
      code,
      compilerVersion,
      platform,
      args,
      hiddenDependencies,
      schema,
      null,
      frontendId,
      annotation,
      slug,
      page,
    ).then(function (data) {
      let standardOutput = '';
      let standardOutputRaw = '';
      let sqlOutput = null;
      let errorsAndWarnings = flatten(Object.values(data.errors));
      let errors = errorsAndWarnings.filter(
        (error) => error.severity === 'ERROR',
      );
      if (errors.length > 0) {
        standardOutput = processErrors(errors, theme);
      } else {
        switch (platform) {
          case TargetPlatforms.JAVA:
            if (data.text) {
              const processingOutput = processJVMOutputWithSql(data.text, theme);
              standardOutput += processingOutput.standardOutput;
              standardOutputRaw += processingOutput.standardOutputRaw;
              sqlOutput = processingOutput.sqlOutput;
            }
            break;
          case TargetPlatforms.JUNIT:
            if (data.testResults) {
              const processingOutput = processJUnitResults(data.testResults,onTestPassed,onTestFailed);
              standardOutput += processingOutput.standardOutput;
              standardOutputRaw += processingOutput.standardOutputRaw;
            } else {
              const processingOutput = processJVMOutput(data.text || '', theme);
              standardOutput += processingOutput.standardOutput;
              standardOutputRaw += processingOutput.standardOutputRaw;
            }
            break;
        }
      }
      let exceptions = null;
      if (data.exception != null) {
        exceptions = findSecurityException(data.exception);
        exceptions.causes = getExceptionCauses(exceptions);
        exceptions.cause = undefined;
      }
      // If after all things are appended to the output, it's still empty, show a placeholder
      if ((standardOutputRaw == null || standardOutputRaw.trim() == '') && exceptions == null) {
        standardOutput = '[No output]';
      }
      return {
        errors: errorsAndWarnings,
        backendOutput: {standardOutput: standardOutput, sqlOutput: sqlOutput},
        exception: exceptions,
      };
    });
  }

  /**
   * Request for getting list of different completion proposals
   *
   * @param code - string code
   * @param cursor - cursor position in code
   * @param compilerVersion - string kotlin compiler
   * @param hiddenDependencies   - read only additional files
   * @param platform - kotlin platform {@see TargetPlatform}
   * @param callback
   */
  static getAutoCompletion(
    code,
    cursor,
    compilerVersion,
    platform,
    hiddenDependencies,
    callback,
  ) {
    const { line, ch, ...options } = cursor;
    const url = API_URLS.COMPLETE(compilerVersion) + `?line=${line}&ch=${ch}`;
    executeCode(
      url,
      code,
      compilerVersion,
      platform,
      '',
      hiddenDependencies,
      null,
      options,
      null, // No request ID for autocomplete
    ).then((data) => {
      callback(data);
    });
  }

  /**
   * Request for getting errors of current file
   *
   * @param code - string code
   * @param compilerVersion - string kotlin compiler
   * @param platform - kotlin platform {@see TargetPlatform}
   * @param hiddenDependencies   - read only additional files
   * @return {*|PromiseLike<T>|Promise<T>}
   */
  static getHighlight(code, compilerVersion, platform, hiddenDependencies) {
    return executeCode(
      API_URLS.HIGHLIGHT(compilerVersion),
      code,
      compilerVersion,
      platform,
      '',
      hiddenDependencies,
      null,
      null, // No options for highlight
      null, // No request ID for highlight
    ).then((data) => data[DEFAULT_FILE_NAME] || []);
  }
}

function executeCode(
  url,
  code,
  compilerVersion,
  targetPlatform,
  args,
  hiddenDependencies,
  schema,
  options,
  requestId,
  annotation,
  slug,
  page,
) {
  const files = [buildFileObject(code, DEFAULT_FILE_NAME)].concat(
    hiddenDependencies.map((file, index) =>
      buildFileObject(file, `hiddenDependency${index}.kt`),
    ),
  );

  const body = {
    args,
    files,
    confType: targetPlatform.id,
    ...(options || {}),
  };

  // Add schema to body if provided
  if (schema) {
    body.schema = schema;
  }

  // Add annotation to body if provided
  if (annotation) {
    body.annotation = annotation;
  }

  // Add slug to body if provided
  if (slug) {
    body.frontendSlug = slug;
  }

  // Add page to body if provided
  if (page) {
    body.frontendPage = page;
  }

  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
  };

  // Add request ID to headers if provided
  if (requestId) {
    headers['X-Request-ID'] = requestId;
  }

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: headers,
  }).then((response) => response.json());
}

/**
 *
 * Build file object.
 * @param code - string code
 * @param fileName - name of file
 * @returns {{name: string, text: string, publicId: string}} - file object
 */
function buildFileObject(code, fileName) {
  return {
    name: fileName,
    text: code,
    publicId: '',
  };
}
